import { FunctionComponent, useMemo, useState, useEffect } from "react";
import GenericWheelpicker, { GenericWheelpickerType } from "../generic";
import { DropdownItemType } from "../../../../atoms/dropdown/container";

interface DateWheelpickerProps extends GenericWheelpickerType {
  minDate?: Date;
  maxDate?: Date;
  monthsList?: string[]; // Custom month names
  daysList?: string[]; // Custom day labels (1-indexed)
}

const defaultMonthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const defaultDayLabels = Array.from({ length: 31 }, (_, i) => String(i + 1));

const getDaysInMonth = (year: number, month: number) => {
  return new Date(year, month + 1, 0).getDate();
};

const getYearRange = (min: Date, max: Date) => {
  const years = [];
  for (let y = min.getFullYear(); y <= max.getFullYear(); y++) {
    years.push(y);
  }
  return years;
};

export const DateWheelpicker: FunctionComponent<
  Omit<DateWheelpickerProps, "itemWidth">
> = ({ minDate, maxDate, selected, monthsList, daysList, ...props }) => {
  // Defaults
  const today = new Date();
  const min = minDate
    ? new Date(minDate)
    : new Date(today.getFullYear() - 100, 0, 1);
  const max = maxDate
    ? new Date(maxDate)
    : new Date(today.getFullYear() + 10, 11, 31);

  // State for selected values
  const [value, setValue] = useState<DropdownItemType[]>(() => {
    if (selected && selected.length === 3) return selected;
    // Default: today
    return [
      { label: String(today.getDate()), value: String(today.getDate()) },
      {
        label: defaultMonthNames[today.getMonth()],
        value: String(today.getMonth()),
      },
      {
        label: String(today.getFullYear()),
        value: String(today.getFullYear()),
      },
    ];
  });

  // Compute year, month, day selections
  const selectedMonth =
    value[1]?.value !== undefined ? Number(value[1].value) : today.getMonth();
  const selectedYear =
    value[2]?.value !== undefined
      ? Number(value[2].value)
      : today.getFullYear();

  // Use custom or default months/days
  const monthNames =
    monthsList && monthsList.length === 12 ? monthsList : defaultMonthNames;
  const dayLabels =
    daysList && daysList.length >= 28 ? daysList : defaultDayLabels;

  // Generate years, months, and days based on min/max and selection
  const years = useMemo(
    () =>
      getYearRange(min, max).map((y) => ({
        label: String(y),
        value: String(y),
      })),
    [min, max]
  );
  const months = useMemo(() => {
    let start = 0,
      end = 11;
    if (selectedYear === min.getFullYear()) start = min.getMonth();
    if (selectedYear === max.getFullYear()) end = max.getMonth();
    return monthNames
      .slice(start, end + 1)
      .map((m, i) => ({ label: m, value: String(i + start) }));
  }, [selectedYear, min, max, monthNames]);
  const days = useMemo(() => {
    let daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    let start = 1,
      end = daysInMonth;
    // If min/max restricts days for the selected year/month
    if (
      selectedYear === min.getFullYear() &&
      selectedMonth === min.getMonth()
    ) {
      start = min.getDate();
    }
    if (selectedYear === max.getFullYear() && selectedMonth === max.getMonth())
      end = max.getDate();

    return Array.from({ length: end - start + 1 }, (_, i) => ({
      label: dayLabels[i + start - 1] || String(i + start),
      value: String(i + start),
    }));
  }, [selectedYear, selectedMonth, min, max, dayLabels]);

  // Wheelpicker expects: days, months, years
  const data = useMemo(
    () => [{ items: days }, { items: months }, { items: years }],
    [days, months, years]
  );

  // Custom value for display
  const selectedDayObj = days.find((d) => d.value === value[0]?.value);
  const selectedMonthObj = months.find((m) => m.value === value[1]?.value);
  const selectedYearObj = years.find((y) => y.value === value[2]?.value);
  const customValue = `${selectedDayObj?.label || "--"} ${
    selectedMonthObj?.label || "--"
  } ${selectedYearObj?.label || "--"}`;

  useEffect(() => {
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    let start = 1,
      end = daysInMonth;
    if (
      selectedYear === min.getFullYear() &&
      selectedMonth === min.getMonth()
    ) {
      start = min.getDate();
    }
    if (
      selectedYear === max.getFullYear() &&
      selectedMonth === max.getMonth()
    ) {
      end = max.getDate();
    }
    const selectedDay = Number(value[0]?.value);
    if (selectedDay < start || selectedDay > end) {
      // Clamp to end
      setValue([
        { label: String(end), value: String(end) },
        value[1],
        value[2],
      ]);
    }
    // eslint-disable-next-line
  }, [selectedYear, selectedMonth, min, max]);

  return (
    <GenericWheelpicker
      {...props}
      data={data}
      selected={value}
      customValue={customValue}
      onChange={(e: DropdownItemType | DropdownItemType[]) =>
        setValue(e as DropdownItemType[])
      }
      itemWidth="fill"
      textAlignment="center"
    />
  );
};

DateWheelpicker.displayName = "DateWheelpicker";

export default DateWheelpicker;
